/**
 * compass-tool.js — page logic for /compass.
 *
 * The maths lives elsewhere on purpose and is unit-tested there:
 *   compass-core.js  — sensor fusion, tilt/axis choice, accuracy  (tests/compass-core.test.mjs)
 *   geomag-wmm.js    — WMM2025, true north                        (tests/geomag-wmm.test.mjs)
 *   astro-core.js    — sun & moon azimuths                        (tests/astro-core.test.mjs)
 *   geolocation.js   — window.__geo, the shared geolocation primitive
 *   wake-lock.js     — window.__wakeLock
 *
 * This file only turns those numbers into a dial, and it follows one rule that
 * the competition does not: never draw a needle we cannot justify. A relative
 * orientation stream, a missing magnetometer and a disturbed field each get
 * their own honest state instead of a plausible-looking arrow.
 */
(function () {
    'use strict';

    var CFG = window.COMPASS_CFG || {};
    var T = CFG.t || {};
    var tr = function (k, vars) {
        var s = (T[k] != null) ? T[k] : k;
        if (vars) for (var v in vars) s = s.split(':' + v).join(vars[v]);
        return s;
    };
    var $ = function (id) { return document.getElementById(id); };
    var ae = function (ev, p) { try { window.__ae && window.__ae(ev, p || {}); } catch (e) {} };
    var stage = function (name, p) { try { window.__stage && window.__stage(name, p || {}); } catch (e) {} };
    var toast = function (m, t) { try { window.__toast && window.__toast(m, t || 'info'); } catch (e) {} };

    var POINTS = tr('points').split('|');

    // ── State ────────────────────────────────────────────────────────────────
    var core = null;
    var pos = null;            // last GeolocationPosition coords
    var geoWatchId = null;
    var declination = null;
    var field = null;          // full WMM field at our position
    var useTrue = true;
    var lockedBearing = null;
    var targetPoint = null;    // {lat, lon}
    var markPoint = null;      // {lat, lon}
    var lastSpoken = -999;
    var lastVibratePoint = null;
    var magSensor = null;      // Generic Sensor API Magnetometer, when granted
    var magMicroTesla = null;
    var calSectors = null;
    var started = false;

    // ── Dependencies ────────────────────────────────────────────────────────
    // The three modules the start button cannot work without. Sentry MP
    // (/ru/compass): compass-core.js did not arrive on a flaky mobile
    // connection, the tap threw «window.CompassCore is not a constructor», and
    // because `started` was set BEFORE that line the button stayed dead for the
    // rest of the visit. The layout's recovery now retries the tags; when even
    // that fails, the page says so and the button stays live.
    var DEPS = ['CompassCore', 'Geomag', 'AstroCore'];
    function missingDeps(names) {
        return (names || DEPS).filter(function (n) {
            return n === 'CompassCore' ? typeof window.CompassCore !== 'function' : !window[n];
        });
    }
    var depsReported = {};
    function reportMissing(miss, where) {
        if (depsReported[where]) return;          // one diagnostic per place per visit
        depsReported[where] = true;
        ae('tool_diagnostic', { issue: 'dep_missing', missing: miss.join(','), where: where });
    }
    // A page cached before core_failed joined COMPASS_CFG can meet this file:
    // an English sentence beats an empty red box.
    var CORE_FAILED = CFG.core_failed || 'The tool module did not load. Reload the page.';
    function showDepsError(miss, where) {
        var el = $('cmpPermError');
        if (el) { el.hidden = false; el.textContent = CORE_FAILED; el.dataset.deps = '1'; }
        reportMissing(miss, where);
    }
    // A script the layout delivered late (its dynamic retry announces itself):
    // take the «did not load» message down once nothing is missing any more.
    document.addEventListener('timbrica:script-recovered', function () {
        if (missingDeps().length) return;
        var el = $('cmpPermError');
        if (el && el.dataset.deps) { el.hidden = true; el.textContent = ''; delete el.dataset.deps; }
    });

    var STORE = 'compass:v1';
    function saveSettings() {
        try {
            localStorage.setItem(STORE, JSON.stringify({
                useTrue: useTrue,
                night: document.body.classList.contains('cmp-night'),
                fmt: coordFormat,
                mark: markPoint, target: targetPoint, bearing: lockedBearing
            }));
        } catch (e) {}
    }
    function loadSettings() {
        try {
            var s = JSON.parse(localStorage.getItem(STORE) || '{}');
            if (typeof s.useTrue === 'boolean') useTrue = s.useTrue;
            if (s.fmt) coordFormat = s.fmt;
            if (s.mark) markPoint = s.mark;
            if (s.target) targetPoint = s.target;
            if (typeof s.bearing === 'number') lockedBearing = s.bearing;
            return s;
        } catch (e) { return {}; }
    }

    // ── Formatting ───────────────────────────────────────────────────────────
    var coordFormat = 'dd';
    function fmtDeg(d) { return (d == null || !isFinite(d)) ? '—' : Math.round(d) + '°'; }
    function pointName(d) { return POINTS[window.CompassCore.pointIndex(d)] || ''; }
    function fmtDist(m) {
        if (m == null || !isFinite(m)) return '—';
        if (m < 1000) return Math.round(m) + ' ' + (CFG.unit_m || 'm');
        return (m / 1000).toFixed(m < 10000 ? 2 : 1) + ' ' + (CFG.unit_km || 'km');
    }
    function toDMS(v, isLat) {
        var hemi = isLat ? (v >= 0 ? 'N' : 'S') : (v >= 0 ? 'E' : 'W');
        v = Math.abs(v);
        var d = Math.floor(v), m = Math.floor((v - d) * 60), s = ((v - d) * 60 - m) * 60;
        return d + '°' + String(m).padStart(2, '0') + '′' + s.toFixed(1) + '″ ' + hemi;
    }
    function fmtCoords(lat, lon) {
        if (lat == null) return '—';
        return coordFormat === 'dms'
            ? toDMS(lat, true) + '  ' + toDMS(lon, false)
            : lat.toFixed(5) + ', ' + lon.toFixed(5);
    }

    // ── Dial construction ────────────────────────────────────────────────────
    // Built once in JS rather than 72 hand-written SVG nodes in the blade:
    // the tick geometry is the same loop nine locales over, and the cardinal
    // letters have to come from the translation file anyway.
    function buildRose(g) {
        if (!g) return null;
        var parts = [];
        for (var a = 0; a < 360; a += 5) {
            var major = (a % 30 === 0), mid = (a % 10 === 0);
            var len = major ? 16 : (mid ? 10 : 6);
            var w = major ? 2.4 : 1.2;
            var cls = major ? 'cmp-tick cmp-tick-major' : 'cmp-tick';
            parts.push('<line x1="150" y1="' + (18) + '" x2="150" y2="' + (18 + len) + '" ' +
                'transform="rotate(' + a + ' 150 150)" stroke-width="' + w + '" class="' + cls + '"/>');
        }
        for (var b = 0; b < 360; b += 30) {
            var label = (b % 90 === 0)
                ? [tr('letter_n'), tr('letter_e'), tr('letter_s'), tr('letter_w')][b / 90]
                : String(b);
            var isCard = (b % 90 === 0);
            var y = isCard ? 56 : 52;
            parts.push('<text x="150" y="' + y + '" text-anchor="middle" ' +
                'data-a="' + b + '" data-y="' + y + '" ' +
                'transform="rotate(' + b + ' 150 150)" ' +
                'class="cmp-rose-label' + (b === 0 ? ' cmp-rose-north' : '') + (isCard ? ' cmp-rose-card' : '') + '">' +
                label + '</text>');
        }
        g.innerHTML = parts.join('');
        return g.querySelectorAll('text');
    }

    /**
     * Keep every label horizontal while the card turns.
     * A physical compass card carries its letters round with it, so at 200° the
     * "S" hangs upside down. That is authentic and it is also unreadable, which
     * is the wrong trade for a tool people squint at in the rain — so each
     * label is counter-rotated about its own anchor: positioned by the card,
     * levelled against it.
     */
    var roseLabels = null;      // the live compass
    var sunRoseLabels = null;   // the compass set by the sun
    function levelLabels(labels, heading) {
        if (!labels) return;
        for (var i = 0; i < labels.length; i++) {
            var el = labels[i];
            var a = +el.getAttribute('data-a'), y = +el.getAttribute('data-y');
            el.setAttribute('transform', 'rotate(' + a + ' 150 150) rotate(' + (heading - a) + ' 150 ' + y + ')');
        }
    }

    function setMarker(id, azimuth, visible, anchorY, heading) {
        var el = $(id);
        if (!el) return;
        if (!visible || azimuth == null || !isFinite(azimuth)) { el.setAttribute('opacity', '0'); return; }
        el.setAttribute('opacity', '1');
        var t = 'rotate(' + azimuth + ' 150 150)';
        // Glyph markers get levelled the same way the labels do; the triangular
        // target marker is symmetric and must stay pointing outward.
        if (anchorY != null) t += ' rotate(' + (heading - azimuth) + ' 150 ' + anchorY + ')';
        el.setAttribute('transform', t);
    }

    // ── Rendering ────────────────────────────────────────────────────────────
    var lastRender = 0;
    function render(now) {
        requestAnimationFrame(render);
        if (now - lastRender < 33) return;   // 30 fps is plenty for a needle
        lastRender = now;
        if (!core || !core.state.ready) return;

        var s = core.state;
        var heading = useTrue ? s.headingTrue : s.headingMagnetic;
        if (heading == null) heading = s.headingMagnetic;
        if (heading == null) return;

        // The rose turns anticlockwise under a fixed index at the top: that is
        // how a real compass card behaves, and it keeps north where north is.
        var rose = $('cmpRose');
        if (rose) rose.setAttribute('transform', 'rotate(' + (-heading) + ' 150 150)');

        $('cmpHeading').textContent = Math.round(heading) + '°';
        $('cmpPoint').textContent = pointName(heading);

        // Sun / moon / target markers live in the rotating rose's frame, so
        // they are set as absolute azimuths and inherit the rose rotation.
        if (pos) {
            var d = new Date();
            var sunAz = window.AstroCore.sunAzimuthDeg(d, pos.latitude, pos.longitude);
            var sunAlt = window.AstroCore.sunAltitudeDeg(d, pos.latitude, pos.longitude);
            var moonAz = window.AstroCore.moonAzimuthDeg(d, pos.latitude, pos.longitude);
            var moonAlt = window.AstroCore.moonAltitudeDeg(d, pos.latitude, pos.longitude);
            // Azimuths from astronomy are TRUE; when the dial shows magnetic
            // north the markers must be shifted by the declination or they
            // would sit in the wrong place by exactly the correction we made
            // such a point of getting right.
            var shift = useTrue ? 0 : -(declination || 0);
            setMarker('cmpSun', sunAz + shift, sunAlt > -6, 66, heading);
            setMarker('cmpMoon', moonAz + shift, moonAlt > -2, 84, heading);
        }
        setMarker('cmpTargetMark', lockedBearing, lockedBearing != null);
        levelLabels(roseLabels, heading);

        // Deviation bar for a held bearing.
        if (lockedBearing != null) {
            var diff = window.CompassCore.angleDiff(lockedBearing, heading);
            var bar = $('cmpDevFill');
            if (bar) {
                var pct = Math.max(-1, Math.min(1, diff / 90));
                bar.style.transform = 'translateX(' + (pct * 50) + '%)';
                bar.style.background = Math.abs(diff) < 3 ? 'var(--success)' : 'var(--accent)';
            }
            $('cmpDevText').textContent = Math.abs(diff) < 3 ? tr('bearing_on_course')
                : (diff < 0 ? tr('bearing_left', { deg: Math.round(-diff) }) : tr('bearing_right', { deg: Math.round(diff) }));
        }

        // Axis / tilt chip.
        var axisEl = $('cmpAxis');
        if (axisEl) {
            axisEl.textContent = (s.axis === 'sight') ? tr('axis_sight') : tr('axis_flat');
            axisEl.className = 'cmp-chip cmp-chip-axis' + (s.axis === 'sight' ? ' is-sight' : '');
        }
        var hintEl = $('cmpAxisHint');
        if (hintEl) hintEl.textContent = (s.axis === 'sight') ? tr('axis_sight_hint') : tr('axis_flat_hint');
        var level = $('cmpLevelDot');
        if (level) {
            // Bubble offset from level, clamped to its track: purely a
            // "hold it flat" cue, so ±25° of pitch fills the whole travel.
            var t = Math.max(-25, Math.min(25, s.topElevation));
            level.setAttribute('cy', String(192 - t * 0.26));
        }

        // Accuracy + interference.
        var acc = $('cmpAccuracy');
        if (acc) {
            acc.textContent = (s.accuracyDeg == null) ? tr('accuracy_unknown') : tr('accuracy_value', { deg: s.accuracyDeg });
        }
        var warn = $('cmpInterference');
        if (warn) {
            var show = s.interference;
            warn.hidden = !show;
            if (show) {
                $('cmpInterferenceText').textContent = tr('interference_text', { deg: s.accuracyDeg });
                if (!warn.dataset.reported) {
                    warn.dataset.reported = '1';
                    ae('tool_diagnostic', { issue: 'magnetic_interference', spread: s.accuracyDeg });
                }
            }
        }

        // Speech / vibration cues.
        maybeCue(heading);
        // Calibration coverage feeds off real headings, so it works on every
        // device — not only those exposing the raw magnetometer.
        if (calSectors) noteCalibration(s.raw);
    }

    function maybeCue(heading) {
        if ($('cmpOptVibrate') && $('cmpOptVibrate').checked && navigator.vibrate) {
            var idx = Math.round(heading / 90) % 4;
            var near = Math.abs(window.CompassCore.angleDiff(heading, idx * 90)) < 2;
            if (near && lastVibratePoint !== idx) { lastVibratePoint = idx; try { navigator.vibrate(35); } catch (e) {} }
            else if (!near && Math.abs(window.CompassCore.angleDiff(heading, idx * 90)) > 6) lastVibratePoint = null;
        }
        if ($('cmpOptSpeak') && $('cmpOptSpeak').checked && window.speechSynthesis) {
            if (Math.abs(window.CompassCore.angleDiff(heading, lastSpoken)) > 15) {
                lastSpoken = heading;
                try {
                    var u = new SpeechSynthesisUtterance(tr('speak_heading', { deg: Math.round(heading), point: pointName(heading) }));
                    u.lang = CFG.locale || 'en';
                    u.rate = 1.05;
                    speechSynthesis.cancel();
                    speechSynthesis.speak(u);
                } catch (e) {}
            }
        }
    }

    // ── Calibration coverage ────────────────────────────────────────────────
    function startCalibration() {
        calSectors = new Array(16).fill(false);
        drawCalibration();
        $('cmpCalModal').hidden = false;
        ae('feature_use', { action: 'calibrate_open' });
    }
    function noteCalibration(raw) {
        if (raw == null) return;
        var i = window.CompassCore.pointIndex(raw);
        if (!calSectors[i]) { calSectors[i] = true; drawCalibration(); }
    }
    function drawCalibration() {
        var done = calSectors.filter(Boolean).length;
        $('cmpCalProgress').textContent = tr('calibrate_progress', { done: done, total: 16 });
        var host = $('cmpCalSectors');
        if (host) {
            var html = '';
            for (var i = 0; i < 16; i++) {
                html += '<div class="cmp-cal-sector' + (calSectors[i] ? ' is-done' : '') + '"></div>';
            }
            host.innerHTML = html;
        }
        if (done === 16) {
            $('cmpCalDone').hidden = false;
            $('cmpCalDone').textContent = tr('calibrate_done', { deg: core && core.state.accuracyDeg != null ? core.state.accuracyDeg : '—' });
            ae('feature_use', { action: 'calibrate_complete' });
        }
    }

    // ── Magnetometer panel ──────────────────────────────────────────────────
    /**
     * Raw field strength, when the browser will give it. This is deliberately
     * NOT a separate tool: as a search term "magnetometer" is mostly about
     * space-weather indices, but as a panel inside a compass it answers a real
     * question — "is this reading disturbed, and by what?" — and doubles as a
     * finder for magnets and ferrous metal.
     *
     * The Generic Sensor API is Chromium-only and normally sits behind a flag,
     * so the honest default is to say what we cannot read rather than leave an
     * empty box. The compass never depends on it: interference detection also
     * works through heading dispersion, which every device provides.
     */
    function initMagnetometer() {
        var box = $('cmpMagBox');
        if (!box) return;
        if (typeof window.Magnetometer !== 'function') { showMagUnavailable(); return; }
        var start = function () {
            try {
                magSensor = new window.Magnetometer({ frequency: 10, referenceFrame: 'device' });
                magSensor.addEventListener('reading', function () {
                    magMicroTesla = Math.sqrt(magSensor.x * magSensor.x + magSensor.y * magSensor.y + magSensor.z * magSensor.z);
                    renderMag();
                });
                magSensor.addEventListener('error', function () { showMagUnavailable(); });
                magSensor.start();
                ae('feature_use', { action: 'magnetometer_on' });
            } catch (e) { showMagUnavailable(); }
        };
        if (navigator.permissions && navigator.permissions.query) {
            navigator.permissions.query({ name: 'magnetometer' })
                .then(function (p) { if (p.state === 'denied') showMagUnavailable(); else start(); })
                .catch(start);
        } else start();
    }
    function showMagUnavailable() {
        $('cmpMagValue').textContent = '—';
        $('cmpMagNote').textContent = tr('mag_unavailable');
    }
    function renderMag() {
        if (magMicroTesla == null) return;
        $('cmpMagValue').textContent = magMicroTesla.toFixed(1) + ' µT';
        var expected = field ? field.f / 1000 : null;   // WMM works in nT
        if (expected) {
            $('cmpMagExpected').textContent = tr('mag_expected', { v: expected.toFixed(1) });
            var ratio = magMicroTesla / expected;
            var off = Math.abs(ratio - 1);
            $('cmpMagNote').textContent = off > 0.25
                ? tr('mag_disturbed', { pct: Math.round(off * 100) })
                : tr('mag_clean');
            var bar = $('cmpMagBar');
            if (bar) bar.style.width = Math.max(2, Math.min(100, ratio * 50)) + '%';
        } else {
            $('cmpMagNote').textContent = tr('mag_need_gps');
        }
    }

    // ── Position, declination, sky ──────────────────────────────────────────
    function onPosition(p) {
        pos = p.coords;
        geoFailed = false;
        if (geoExp) { geoExp = null; showGeoProblem(); }
        field = window.Geomag.field(pos.latitude, pos.longitude, (pos.altitude || 0) / 1000, new Date());
        declination = field.declination;
        if (core) core.setDeclination(declination);
        syncModeLabel();          // истинный север стал настоящим — скажем об этом

        $('cmpDeclValue').textContent = (declination >= 0 ? '+' : '') + declination.toFixed(1) + '° ' +
            (declination >= 0 ? tr('declination_east') : tr('declination_west'));
        $('cmpModelNote').textContent = tr('model_note', { model: window.Geomag.MODEL });
        if (!field.inRange) {
            $('cmpModelNote').textContent = tr('model_expired', { year: window.Geomag.VALID_UNTIL });
        }

        $('cmpCoords').textContent = fmtCoords(pos.latitude, pos.longitude);
        $('cmpGpsAcc').textContent = pos.accuracy != null ? tr('gps_accuracy', { m: Math.round(pos.accuracy) }) : '';
        $('cmpGpsAlt').textContent = pos.altitude != null ? tr('gps_altitude', { m: Math.round(pos.altitude) }) : '';
        // Course over ground: an independent heading that needs no magnetometer.
        // Only meaningful while actually moving — a parked phone's GPS course
        // is noise, and printing it would be the very sin we are avoiding.
        var moving = (pos.speed != null && pos.speed > 1.5 && pos.heading != null && isFinite(pos.heading));
        $('cmpGpsCourse').textContent = moving ? tr('gps_course', { deg: Math.round(pos.heading) }) : '';
        $('cmpGpsCourse').hidden = !moving;
        if (moving) reportGroundTruth(pos.heading);

        renderSky();
        renderTarget();
        renderMag();
        renderSunDial();
        $('cmpGeoBlock').hidden = false;
        // One stage per visit: watchPosition answers every few seconds, and the
        // funnel used to log «located → located» on each fix (6 events in 46 s
        // in the 12.10.2026 report), burying the stages that mean something.
        if (!locatedStaged) { locatedStaged = true; stage('located'); }
    }
    var locatedStaged = false;

    /**
     * Field check against something we did not compute ourselves.
     *
     * Two of this tool's load-bearing assumptions cannot be verified in a test:
     * that iOS reports a MAGNETIC heading (so declination must be added), and
     * that the Android absolute stream is oriented the way we think. GPS course
     * over ground is an independent truth — no magnetometer involved — so when
     * the user is genuinely moving we log the disagreement once per session.
     * A systematic offset the size of the local declination would show up
     * immediately in aggregate, on one platform and not the other.
     *
     * Sampled once, only while walking or faster, and only the delta is sent.
     */
    var groundTruthSent = false;
    function reportGroundTruth(gpsCourse) {
        if (groundTruthSent || !core || !core.state.ready || declination == null) return;
        var mine = core.state.headingTrue;
        if (mine == null) return;
        groundTruthSent = true;
        ae('tool_diagnostic', {
            issue: 'heading_vs_gps_course',
            delta: Math.round(window.CompassCore.angleDiff(mine, gpsCourse)),
            decl: Math.round(declination * 10) / 10,
            source: core.state.source,
            axis: core.state.axis,
            acc: core.state.accuracyDeg
        });
    }

    function renderSky() {
        if (!pos) return;
        var skyMiss = missingDeps(['AstroCore', 'CompassCore']);
        if (skyMiss.length) { reportMissing(skyMiss, 'sky'); return; }
        var now = new Date();
        var sunAz = window.AstroCore.sunAzimuthDeg(now, pos.latitude, pos.longitude);
        var sunAlt = window.AstroCore.sunAltitudeDeg(now, pos.latitude, pos.longitude);
        var moonAz = window.AstroCore.moonAzimuthDeg(now, pos.latitude, pos.longitude);
        var rs = window.AstroCore.sunTimeAzimuths(now, pos.latitude, pos.longitude);

        $('cmpSunAz').textContent = tr('sun_azimuth', { deg: Math.round(sunAz) }) +
            ' · ' + (sunAlt > 0 ? tr('sun_altitude', { deg: Math.round(sunAlt) }) : tr('sun_below'));
        $('cmpMoonAz').textContent = tr('moon_azimuth', { deg: Math.round(moonAz) });
        $('cmpRiseAz').textContent = rs.sunrise != null ? tr('sunrise_az', { deg: Math.round(rs.sunrise) }) : '';
        $('cmpSetAz').textContent = rs.sunset != null ? tr('sunset_az', { deg: Math.round(rs.sunset) }) : '';

        // The no-magnetometer path: tell the user where the sun IS, so they can
        // orient themselves without any sensor at all.
        var line = $('cmpSunMethodNow');
        if (line) {
            if (sunAlt > 0) {
                line.textContent = tr('sunmethod_now', { deg: Math.round(sunAz), point: pointName(sunAz) }) +
                    ' ' + tr('sunmethod_shadow', { deg: Math.round((sunAz + 180) % 360), point: pointName((sunAz + 180) % 360) });
            } else if (window.AstroCore.moonAltitudeDeg(now, pos.latitude, pos.longitude) > 0) {
                line.textContent = tr('sunmethod_night', { deg: Math.round(moonAz), point: pointName(moonAz) });
            } else {
                // The moon is down too: naming its azimuth as «the landmark» pointed at the ground.
                line.textContent = tr('sundial_dark') + ' ' + tr(pos.latitude >= 0 ? 'sundial_polaris' : 'sundial_southern_cross');
            }
        }
    }

    // ── The compass set by the sun ──────────────────────────────────────────
    /**
     * For a device with no magnetometer (9 % of the sessions that allowed the
     * sensors, RU shard, 60 days to 2026-10-16 — 59 % on tablets), a desktop,
     * or a browser that gives only a relative stream. The sun's azimuth is
     * known from the time and the place (astro-core.js, 10 183 reference
     * checks), so a dial turned to put the sun under the index IS a compass the
     * moment the top of the screen faces the sun — and its north is TRUE north,
     * no declination needed. Shadow mode turns it 180°: the top along the
     * shadow, object to tip. At night the moon takes the sun's place.
     */
    var sunMode = 'sun';                 // 'sun' | 'shadow'
    var sunShownSent = false;
    function fallbackShown() { var fb = $('cmpFallback'); return !!(fb && !fb.hidden); }
    function renderSunDial() {
        var wrap = $('cmpSunDialWrap'), need = $('cmpSunNeedPlace');
        if (!wrap) return;
        if (!pos) {
            wrap.hidden = true;
            if (need) need.hidden = !fallbackShown();
            return;
        }
        if (need) need.hidden = true;
        var miss = missingDeps(['AstroCore', 'CompassCore']);
        if (miss.length) { wrap.hidden = true; reportMissing(miss, 'sundial'); return; }

        var now = new Date(), lat = pos.latitude, lon = pos.longitude;
        var sunAz = window.AstroCore.sunAzimuthDeg(now, lat, lon);
        var sunAlt = window.AstroCore.sunAltitudeDeg(now, lat, lon);
        var moonAz = window.AstroCore.moonAzimuthDeg(now, lat, lon);
        var moonAlt = window.AstroCore.moonAltitudeDeg(now, lat, lon);
        var body = sunAlt > 0 ? sunMode : (moonAlt > 0 ? 'moon' : 'none');
        var night = $('cmpSunMethodNow');

        if (body === 'none') {
            wrap.hidden = true;
            if (night) night.textContent = tr('sundial_dark') + ' ' + tr(lat >= 0 ? 'sundial_polaris' : 'sundial_southern_cross');
            return;
        }
        var heading = body === 'shadow' ? window.CompassCore.norm360(sunAz + 180) : (body === 'moon' ? moonAz : sunAz);
        wrap.hidden = false;
        var rose = $('cmpSunRose');
        if (rose) rose.setAttribute('transform', 'rotate(' + (-heading) + ' 150 150)');
        levelLabels(sunRoseLabels, heading);
        $('cmpSunHeading').textContent = Math.round(heading) + '°';
        $('cmpSunPoint').textContent = pointName(heading);
        $('cmpSunGlyph').textContent = body === 'moon' ? '☾' : (body === 'sun' ? '☀' : '');
        $('cmpSunModes').hidden = (body === 'moon');
        $('cmpSunBySun').setAttribute('aria-pressed', String(sunMode === 'sun'));
        $('cmpSunByShadow').setAttribute('aria-pressed', String(sunMode === 'shadow'));
        $('cmpSunAim').textContent = tr(body === 'moon' ? 'sundial_aim_moon' : (body === 'shadow' ? 'sundial_aim_shadow' : 'sundial_aim_sun'));
        // A sun near the zenith has an azimuth but no direction you can aim at.
        $('cmpSunNote').textContent = (body !== 'moon' && sunAlt > 70)
            ? tr('sundial_high', { deg: Math.round(sunAlt) }) : tr('sundial_true');
        if (!sunShownSent && fallbackShown()) {
            sunShownSent = true;
            ae('feature_use', { action: 'sundial_shown', body: body, alt: Math.round(body === 'moon' ? moonAlt : sunAlt) });
        }
    }
    // The sun moves 15° an hour; a quarter-minute refresh keeps the dial honest.
    setInterval(function () { if (fallbackShown()) renderSunDial(); }, 15000);

    function renderTarget() {
        if (!targetPoint || !pos) { $('cmpTargetResult').textContent = ''; return; }
        var b = window.CompassCore.bearingTo(pos.latitude, pos.longitude, targetPoint.lat, targetPoint.lon);
        var d = window.CompassCore.distanceTo(pos.latitude, pos.longitude, targetPoint.lat, targetPoint.lon);
        $('cmpTargetResult').textContent = tr('target_result', { dist: fmtDist(d), deg: Math.round(b) });
        lockedBearing = useTrue ? b : window.CompassCore.norm360(b - (declination || 0));
        showBearing();
    }

    function renderMark() {
        if (!markPoint || !pos) { $('cmpMarkResult').textContent = ''; return; }
        var b = window.CompassCore.bearingTo(pos.latitude, pos.longitude, markPoint.lat, markPoint.lon);
        var d = window.CompassCore.distanceTo(pos.latitude, pos.longitude, markPoint.lat, markPoint.lon);
        $('cmpMarkResult').textContent = tr('mark_back', { dist: fmtDist(d), deg: Math.round(b) });
    }

    function showBearing() {
        var has = lockedBearing != null;
        $('cmpBearingPanel').hidden = !has;
        if (!has) return;
        $('cmpBearingValue').textContent = tr('bearing_locked', { deg: Math.round(lockedBearing) });
        $('cmpBackAz').textContent = tr('back_azimuth', { deg: Math.round(window.CompassCore.backAzimuth(lockedBearing)) });
        saveSettings();
    }

    // ── Start-up ─────────────────────────────────────────────────────────────
    /* Компас по солнцу — там, где стрелки нет: компьютер, устройство без
       магнитометра, относительный поток, отказ в доступе. Не рядом с кнопкой
       «Включить компас»: там у него своя кнопка «Определить место» спорила бы
       с главной, а стрелка ещё может заработать. */
    var FALLBACK_STATES = ['cmpDesktop', 'cmpRelative', 'cmpNoSensor'];
    function showState(which, withFallback) {
        ['cmpPerm', 'cmpDesktop', 'cmpRelative', 'cmpNoSensor', 'cmpMain'].forEach(function (id) {
            var el = $(id);
            if (el) el.hidden = (id !== which);
        });
        var fb = $('cmpFallback');
        if (fb) fb.hidden = !(withFallback || FALLBACK_STATES.indexOf(which) >= 0);
        if (geoExp) showGeoProblem();
        renderSunDial();
    }

    function beginSensors() {
        if (started) return;
        var miss = missingDeps();
        if (miss.length) { showDepsError(miss, 'sensors'); return; }   // not latched: the next tap asks again
        core = new window.CompassCore({ smooth: 0.22 });
        started = true;                                                  // latched only once the core exists
        var permErr = $('cmpPermError');
        if (permErr && permErr.dataset.deps) { permErr.hidden = true; delete permErr.dataset.deps; }
        if (declination != null) core.setDeclination(declination);

        core.start().then(function (res) {
            ae('feature_use', { action: 'permission', result: res.permission });
            if (res.permission === 'denied') {
                $('cmpPermError').hidden = false;
                $('cmpPermError').textContent = tr('perm_denied');
                started = false;
                showState('cmpPerm', true);     // без датчиков остаётся солнце
                return;
            }
            if (res.permission === 'unsupported') { showState('cmpNoSensor'); stage('no_sensor'); return; }
            if (res.permission === 'error') {
                $('cmpPermError').hidden = false;
                $('cmpPermError').textContent = tr('perm_error', { msg: res.error || '' });
                started = false;
                showState('cmpPerm', true);
                return;
            }
            showState('cmpMain');
            stage('sensor_on');
            initMagnetometer();
            if (useTrue) initGeo();          // координаты нужны только истинному северу

            // Verdict: first after 2.5 s — the difference between "no data
            // yet" and "this browser will never give us an absolute heading" —
            // then again whenever the stream says something new. The first
            // version judged once: a stream that started at 3 s left the page
            // on «no magnetometer» for good while the needle data flowed.
            var t0 = Date.now(), verdict = null;
            function judge() {
                var s = core.state;
                var v = !s.ready ? 'no_sensor' : (!s.absolute ? 'relative_only' : 'compass_live');
                if (v === verdict) return;
                var late = verdict !== null;
                verdict = v;
                showState(v === 'no_sensor' ? 'cmpNoSensor' : (v === 'relative_only' ? 'cmpRelative' : 'cmpMain'));
                stage(v);
                var p = { action: v };
                if (v === 'compass_live') p.source = s.source;
                if (late) p.late_ms = Date.now() - t0;
                // What the browser DID deliver: motion without orientation is a
                // device without the sensors; nothing at all is a browser or a
                // webview that passes none (12.10.2026 report: Android 8.1
                // webview — until now indistinguishable).
                if (v === 'no_sensor' && core.evidence) {
                    p.motion = core.evidence.motion;
                    p.null_events = core.evidence.orientNull;
                }
                ae('feature_use', p);
            }
            setTimeout(function () {
                judge();
                core.onUpdate(judge);
            }, 2500);
        });
    }


    /* Подпись режима обязана называть то, что стрелка показывает СЕЙЧАС.
       Рендер молча откатывается на магнитный курс, когда истинного нет
       (`if (heading == null) heading = s.headingMagnetic`), — а кнопка при
       этом продолжала говорить «Истинный». В Москве это расхождение 12°, на
       севере России до 20°: человек сверяет с картой и видит ошибку, которой
       мы не признаём. Пока склонение не приехало, называем вещи своими
       именами и показываем, чего не хватает. */
    function syncModeLabel() {
        var modeBtn = $('cmpModeToggle');
        if (!modeBtn) return;
        var истинныйДоступен = (declination != null);
        var показываемИстинный = useTrue && истинныйДоступен;
        modeBtn.textContent = показываемИстинный ? tr('mode_true') : tr('mode_magnetic');
        modeBtn.classList.toggle('is-magnetic', !показываемИстинный);
        modeBtn.classList.toggle('is-pending', useTrue && !истинныйДоступен);
    }

    /* Где сказать о беде с геолокацией: в видимом блоке. Раньше ответ всегда
       писался в панель живого компаса — и на устройстве без магнитометра, где
       тот скрыт, отказ геолокации не видел никто. */
    function geoHost() { return fallbackShown() ? $('cmpGeoPanelFallback') : $('cmpGeoPanel'); }
    // The explanation outlives the state it arrived in: a refusal comes back
    // within milliseconds, while the live card is still up, and the verdict
    // «no sensor» hides that card 2.5 s later. Kept, and redrawn where visible.
    var geoExp = null;
    function showGeoProblem() {
        ['cmpGeoPanel', 'cmpGeoPanelFallback'].forEach(function (id) {
            var el = $(id);
            if (el && el.classList.contains('geo-panel')) { el.textContent = ''; el.classList.remove('geo-panel'); }
        });
        if (geoExp && window.__geo) window.__geo.panel(geoHost(), geoExp);
    }
    var geoFailed = false;
    function initGeo() {
        if (!window.__geo) return;
        // One watch at a time; a watch that reported an error is replaced when
        // asked again (a timeout keeps the old one trying, a retry starts fresh).
        if (geoWatchId != null && !geoFailed) return;
        if (geoWatchId != null) { window.__geo.clear(geoWatchId); geoWatchId = null; }
        geoFailed = false;
        var av = window.__geo.available();
        if (!av.ok) {
            geoExp = window.__geo.explain(av.reason);
            showGeoProblem();
            return;
        }
        geoWatchId = window.__geo.watch(onPosition, function (err) {
            geoFailed = true;
            geoExp = window.__geo.explain(err);
            showGeoProblem();
        }, { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 });
    }

    // ── Wiring ───────────────────────────────────────────────────────────────
    function bind() {
        // Every listener is attached BEFORE any early return below: an init()
        // that can bail out above its own binding block silently kills every
        // button on the page (the /share-location lesson).
        var permBtn = $('cmpPermBtn');
        if (permBtn) permBtn.addEventListener('click', beginSensors);

        var modeBtn = $('cmpModeToggle');
        if (modeBtn) modeBtn.addEventListener('click', function () {
            useTrue = !useTrue;
            /* Просят истинный север, а координат нет — вот теперь запрос
               уместен: человек сам сказал, что ему нужно. */
            if (useTrue && declination == null) initGeo();
            syncModeLabel();
            saveSettings();
            ae('feature_use', { action: 'mode', value: useTrue ? 'true' : 'magnetic' });
        });

        var takeBtn = $('cmpBearingTake');
        if (takeBtn) takeBtn.addEventListener('click', function () {
            if (!core || !core.state.ready) return;
            lockedBearing = useTrue ? core.state.headingTrue : core.state.headingMagnetic;
            if (lockedBearing == null) lockedBearing = core.state.headingMagnetic;
            showBearing();
            ae('feature_use', { action: 'bearing_take' });
        });
        var clearBtn = $('cmpBearingClear');
        if (clearBtn) clearBtn.addEventListener('click', function () {
            lockedBearing = null; targetPoint = null;
            $('cmpBearingPanel').hidden = true;
            $('cmpTargetResult').textContent = '';
            saveSettings();
        });
        var manual = $('cmpBearingManual');
        if (manual) manual.addEventListener('change', function () {
            var v = parseFloat(manual.value);
            if (isFinite(v)) { lockedBearing = window.CompassCore.norm360(v); showBearing(); ae('feature_use', { action: 'bearing_manual' }); }
        });

        var targetBtn = $('cmpTargetSet');
        if (targetBtn) targetBtn.addEventListener('click', function () {
            var la = parseFloat($('cmpTargetLat').value), lo = parseFloat($('cmpTargetLon').value);
            if (!isFinite(la) || !isFinite(lo) || Math.abs(la) > 90 || Math.abs(lo) > 180) {
                toast(tr('target_invalid'), 'error'); return;
            }
            if (!pos) { toast(tr('target_need_gps'), 'warning'); return; }
            targetPoint = { lat: la, lon: lo };
            renderTarget(); saveSettings();
            ae('feature_use', { action: 'target_set' });
        });

        var markBtn = $('cmpMarkHere');
        if (markBtn) markBtn.addEventListener('click', function () {
            if (!pos) { toast(tr('target_need_gps'), 'warning'); return; }
            markPoint = { lat: pos.latitude, lon: pos.longitude };
            toast(tr('mark_saved'), 'success');
            renderMark(); saveSettings();
            ae('feature_use', { action: 'mark_here' });
        });
        var markClear = $('cmpMarkClear');
        if (markClear) markClear.addEventListener('click', function () {
            markPoint = null; $('cmpMarkResult').textContent = ''; saveSettings();
        });

        var calBtn = $('cmpCalBtn');
        if (calBtn) calBtn.addEventListener('click', startCalibration);
        var calClose = $('cmpCalClose');
        if (calClose) calClose.addEventListener('click', function () { $('cmpCalModal').hidden = true; calSectors = null; });

        var fmtBtn = $('cmpCoordFormat');
        if (fmtBtn) fmtBtn.addEventListener('click', function () {
            coordFormat = (coordFormat === 'dd') ? 'dms' : 'dd';
            fmtBtn.textContent = coordFormat.toUpperCase();
            if (pos) $('cmpCoords').textContent = fmtCoords(pos.latitude, pos.longitude);
            saveSettings();
        });
        var copyBtn = $('cmpCopyCoords');
        if (copyBtn) copyBtn.addEventListener('click', function () {
            if (!pos) return;
            var text = fmtCoords(pos.latitude, pos.longitude);
            try { navigator.clipboard.writeText(text); toast(tr('coords_copied'), 'success'); } catch (e) {}
            ae('feature_use', { action: 'copy_coords' });
        });

        var night = $('cmpOptNight');
        if (night) night.addEventListener('change', function () {
            document.body.classList.toggle('cmp-night', night.checked);
            saveSettings();
            ae('feature_use', { action: 'night', on: night.checked });
        });
        var awake = $('cmpOptAwake');
        if (awake) awake.addEventListener('change', function () {
            if (!window.__wakeLock) return;
            if (awake.checked) window.__wakeLock.request(); else window.__wakeLock.release();
            ae('feature_use', { action: 'wake_lock', on: awake.checked });
        });
        var cam = $('cmpOptCamera');
        if (cam) cam.addEventListener('change', function () {
            if (cam.checked) enableCamera(); else disableCamera();
        });

        // Declination lookup — the part of the page that works on any device,
        // including the desktops that have no magnetometer at all.
        var declBtn = $('cmpDeclBtn');
        if (declBtn) declBtn.addEventListener('click', function () {
            var la = parseFloat($('cmpDeclLat').value), lo = parseFloat($('cmpDeclLon').value);
            if (!isFinite(la) || !isFinite(lo) || Math.abs(la) > 90 || Math.abs(lo) > 180) {
                toast(tr('target_invalid'), 'error'); return;
            }
            var declMiss = missingDeps(['Geomag']);
            if (declMiss.length) { toast(CORE_FAILED, 'error'); reportMissing(declMiss, 'declination'); return; }
            var f = window.Geomag.field(la, lo, 0, new Date());
            $('cmpDeclOut').hidden = false;
            $('cmpDeclOutD').textContent = (f.declination >= 0 ? '+' : '') + f.declination.toFixed(2) + '°';
            $('cmpDeclOutI').textContent = f.inclination.toFixed(2) + '°';
            $('cmpDeclOutF').textContent = Math.round(f.f) + ' nT';
            $('cmpDeclOutChange').textContent = tr('decl_change_val', { val: (f.dDdt >= 0 ? '+' : '') + f.dDdt.toFixed(2) });
            ae('feature_use', { action: 'declination_lookup' });
        });
        var declGps = $('cmpDeclUseGps');
        if (declGps) declGps.addEventListener('click', function () {
            if (!pos) { toast(tr('target_need_gps'), 'warning'); return; }
            $('cmpDeclLat').value = pos.latitude.toFixed(4);
            $('cmpDeclLon').value = pos.longitude.toFixed(4);
            declBtn.click();
        });

        // The compass set by the sun: its own request for a place, its modes.
        var locBtn = $('cmpSunLocate');
        if (locBtn) locBtn.addEventListener('click', function () {
            initGeo();
            ae('feature_use', { action: 'sundial_locate' });
        });
        [['cmpSunBySun', 'sun'], ['cmpSunByShadow', 'shadow']].forEach(function (b) {
            var el = $(b[0]);
            if (el) el.addEventListener('click', function () {
                sunMode = b[1];
                renderSunDial();
                ae('feature_use', { action: 'sundial_mode', value: b[1] });
            });
        });
    }

    var camStream = null;
    function enableCamera() {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { toast(tr('camera_denied'), 'warning'); return; }
        navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } } })
            .then(function (st) {
                camStream = st;
                var v = $('cmpCamVideo');
                v.srcObject = st; v.hidden = false;
                document.body.classList.add('cmp-camera');
                ae('feature_use', { action: 'camera_on' });
            })
            .catch(function () {
                toast(tr('camera_denied'), 'warning');
                var c = $('cmpOptCamera'); if (c) c.checked = false;
            });
    }
    function disableCamera() {
        if (camStream) { camStream.getTracks().forEach(function (t) { t.stop(); }); camStream = null; }
        var v = $('cmpCamVideo');
        if (v) { v.srcObject = null; v.hidden = true; }
        document.body.classList.remove('cmp-camera');
    }

    // ── Init ─────────────────────────────────────────────────────────────────
    function init() {
        bind();                       // FIRST — see the comment in bind()
        roseLabels = buildRose($('cmpRoseTicks'));
        sunRoseLabels = buildRose($('cmpSunRoseTicks'));
        var saved = loadSettings();

        var modeBtn = $('cmpModeToggle');
        if (modeBtn) {
            syncModeLabel();
        }
        if (saved.night && $('cmpOptNight')) { $('cmpOptNight').checked = true; document.body.classList.add('cmp-night'); }
        if ($('cmpCoordFormat')) $('cmpCoordFormat').textContent = coordFormat.toUpperCase();
        // A saved bearing needs CompassCore for its back azimuth; without the
        // module this line used to throw and take the whole start screen with it.
        if (lockedBearing != null && !missingDeps(['CompassCore']).length) showBearing();

        /* ⭐Геолокацию НЕ просим при открытии страницы.
           Замер 14 дней: 1172 сессии из 2570 отказали — половина людей видит
           системный диалог раньше, чем поймёт, зачем он. У отказавших доля
           дошедших до работающего компаса 27.4 % против 39.8 % у остальных.
           Координаты нужны ровно для одного — магнитного склонения, то есть
           истинного севера; сама стрелка работает от магнитометра. Поэтому
           спрашиваем после того, как человек включил компас (initGeo зовётся
           из beginSensors) — в этот момент он уже вовлечён и видит, о чём
           речь. Настройки, где истинный север выключен, не спрашивают вовсе. */
        requestAnimationFrame(render);

        // Desktops have no magnetometer; saying so up front beats a dead dial.
        // Touch is the honest proxy — `window.orientation` and UA sniffing both
        // misfire on tablets and on desktop Chrome's device emulation.
        var mobileLike = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
        if (!mobileLike) { showState('cmpDesktop'); buildQr(); stage('desktop'); return; }
        showState('cmpPerm');
        stage('ready');
    }

    function buildQr() {
        var clean = location.href.split('#')[0].split('?')[0];
        var urlEl = $('cmpDesktopUrl');
        if (urlEl) urlEl.textContent = clean;      // useful even if the lib fails
        var host = $('cmpQr');
        if (!host) return;
        var draw = function () {
            try {
                if (typeof window.qrcode !== 'function') return;
                var q = window.qrcode(0, 'M');
                q.addData(clean);
                q.make();
                host.innerHTML = q.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
            } catch (e) {}
        };
        if (typeof window.qrcode === 'function') { draw(); return; }
        var s = document.createElement('script');
        s.src = '/vendor/qrcode/qrcode.min.js';
        s.async = true;
        s.onload = draw;
        document.head.appendChild(s);
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
})();
